<script setup lang="ts">
import { peekAuthEmbeddedReturn } from '@/lib/authEmbedded';
import { logError } from '@/lib/logError';
import { redirect } from '@/lib/redirect';
import { Block, LayoutGap } from '@dabble/quillui';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import FoundView from '@/views/FoundView/FoundView.vue';
import GenreView from '@/views/GenreView/GenreView.vue';

const router = useRouter();
const currentStep = ref<string>('found');

const handleNext = async () => {
  try {
    if (currentStep.value === 'found') {
      currentStep.value = 'genre';
    } else if (currentStep.value === 'genre') {
      // The survey always ends where the signup was headed: `redirect()` resolves
      // that (the writer app unless a `/billing/*` signup pinned the portal). The
      // flag only says whether this is the first pass. It is cleared by that first
      // hand-off, so someone who comes back to the survey (browser Back from the
      // app, to change an answer) arrives without it. They used to be dropped on
      // the billing portal instead, where a brand-new trialist sees a price and
      // "Cancel Trial" and takes it for the way out.
      await redirect({ created: peekAuthEmbeddedReturn() });
    }
  } catch (error) {
    // A refused hand-off (INVALID_REDIRECT, e.g. a 2.x target — DAB-771) would
    // otherwise leave them on a last step whose button can only fail again.
    logError(error);
    await router.push('/billing/portal');
  }
};

const handleBack = () => {
  currentStep.value = 'found';
};
</script>

<template>
  <LayoutGap direction="row" align="center" vertical-align="center" fill class="onboarding__page">
    <Block variant="canvas" class="onboarding__block">
      <img src="/images/color-top.svg" alt="" class="onboarding__color-top" role="presentation" />
      <Block variant="canvas" :padding="['medium', 'medium']">
        <LayoutGap direction="column" align="center" size="small" fill>
          <div class="onboarding__logo-wrapper">
            <img src="/images/dabble-logo.svg" alt="Dabble" />
          </div>
          <div class="onboarding__step">
            <FoundView v-if="currentStep === 'found'" @next="handleNext" />
            <GenreView v-else-if="currentStep === 'genre'" @next="handleNext" @back="handleBack" />
          </div>
        </LayoutGap>
      </Block>
    </Block>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.onboarding {
  &__page {
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    padding: space('medium');
    box-sizing: border-box;
    background-color: theme('color-bg-app-overlay');
  }

  &__block {
    width: min(100%, 50rem);
    overflow: hidden;
    border-bottom-left-radius: borderRadius('large');
    border-bottom-right-radius: borderRadius('large');
  }

  &__color-top {
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: top center;
  }

  &__logo-wrapper {
    height: 64px;
    width: 100%;
    max-width: 160px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__step {
    width: 100%;
  }
}
</style>
